<template>
  <div class="main-panel" :class="{ isMobile: isMobile }">
    <!-- 滚动区域（独立容器） -->
    <div
      class="marquee-wrapper"
      ref="marqueeWrapper"
      @mouseenter="pauseScroll"
      @mouseleave="resumeScroll"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <div
        class="marquee-track"
        ref="marqueeTrack"
        :style="{ transform: `translateX(${translateX}px)` }"
      >
        <!-- 原始行组 -->
        <div class="rows-group">
          <div
            class="card-row"
            v-for="(row, rIndex) in cardRows"
            :key="'orig-row-' + rIndex"
          >
            <div
              class="question-card"
              v-for="(q, qIndex) in row"
              :key="qIndex"
              @click.stop="handleClick(q)"
            >
              <span class="card-text">{{ q }}</span>
            </div>
          </div>
        </div>
        <!-- 克隆行组 -->
        <div class="rows-group">
          <div
            class="card-row"
            v-for="(row, rIndex) in cardRows"
            :key="'clone-row-' + rIndex"
          >
            <div
              class="question-card"
              v-for="(q, qIndex) in row"
              :key="qIndex"
              @click.stop="handleClick(q)"
            >
              <span class="card-text">{{ q }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部分组信息（独立容器），数据为空时不渲染 -->
    <div class="bottom-info" v-if="groupedInfos.length > 0">
      <div
        class="info-group"
        v-for="(group, groupIndex) in groupedInfos"
        :key="groupIndex"
      >
        <span
          class="group-tag"
          :class="{ 'has-tip': hasTip(group.tag) }"
          @click="showTip(group.tag)"
        >{{ group.tag }}</span>
        <div class="group-items">
          <div
            class="info"
            v-for="(item, index) in group.items"
            :key="index"
            @click="sendRequest(item)"
            :title="item"
          >
            {{ item }}
          </div>
        </div>
      </div>
    </div>

    <!-- 自定义提示弹窗 -->
    <transition name="tip-fade">
      <div v-if="tipVisible" class="tip-modal" @click.self="closeTip">
        <div class="tip-dialog">
          <div class="tip-header">
            <span class="tip-title">提示词指南</span>
            <button class="tip-close" @click="closeTip">&times;</button>
          </div>
          <div class="tip-body">{{ tipContent }}</div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import pubsub from "pubsub-js";
import { mapState } from "vuex/dist/vuex.common.js";
import { getExamRecommands, getExerciseRecommands } from "@/api/home";

// 提示词字典（写死）
const TIP_DICT = {
  "指南": "写书提示词：知识点或要求 + 指南|讲义|手册|小说|绘本|散文集 等等，如 `英语语法指南`",
  "诊断": "AI诊断提示词：知识点或要求 + 诊断，如 `二次函数提升诊断`",
  "规划": "提示词：知识点或要求 + 规划，如 `函数最值问题一周学习规划`",
  "讲义": "写书提示词：知识点或要求 + 指南|讲义|手册|小说|绘本|散文集 等等，如 `高二奥数讲义`",
  "测试": "提示词：知识点或要求 + 测试，如 `二次函数专题测试`",
  "图谱": "生成知识图谱提示词：知识点或要求 + 图谱，如 `二次函数图谱`",
  "真题": "检索真题提示词：数量 + 难度 + 知识点 + 真题（缺少真题字段，题目将由AI生成），如 `3道难度较大二次函数真题`",
  "手册": "写书提示词：知识点或要求 + 指南|讲义|手册|小说|绘本|散文集 等等，如 `初三英语语法手册`",
  "任务": "主题任务提示词：马上开辟新的学习任务 + 知识点 + 环节数量 ，如 `马上开辟新的学习任务，摩擦力，5个环节`",
};

export default {
  data() {
    return {
      recommands: {
        questions: [],
        infos: []
      },
      requestId: 0,
      translateX: 0,
      scrollSpeed: 0.8,
      animationId: null,
      isPaused: false,
      touchStartX: 0,
      touchStartTranslateX: 0,
      isDragging: false,
      originalWidth: 0,
      tipVisible: false,
      tipContent: ""
    };
  },
  computed: {
    ...mapState(["isMobile", "robotId"]),
    cardRows() {
      const groups = this.recommands.questions || [];
      if (groups.length === 0) return [];
      const maxLen = Math.max(...groups.map(g => (g.questions || []).length), 0);
      const rows = [];
      for (let i = 0; i < maxLen; i++) {
        const row = [];
        for (const group of groups) {
          const qs = group.questions || [];
          if (i < qs.length) {
            row.push(qs[i]);
          }
        }
        if (row.length > 0) {
          rows.push(row);
        }
      }
      return rows;
    },
    groupedInfos() {
      const infos = this.recommands.infos || [];
      if (infos.length === 0) return [];
      
      const groups = {};
      infos.forEach(item => {
        const tag = item.slice(-2);
        if (!groups[tag]) {
          groups[tag] = [];
        }
        groups[tag].push(item);
      });
      
      return Object.entries(groups)
        .map(([tag, items]) => ({ tag, items }))
        .sort((a, b) => b.items.length - a.items.length);
    }
  },
  methods: {
    sendRequest(q) {
      pubsub.publish("sendRequest", q);
    },
    handleClick(q) {
      if (this.isDragging) return;
      this.sendRequest(q);
    },
    hasTip(tag) {
      return Object.prototype.hasOwnProperty.call(TIP_DICT, tag);
    },
    showTip(tag) {
      if (this.hasTip(tag)) {
        this.tipContent = TIP_DICT[tag];
        this.tipVisible = true;
      }
    },
    closeTip() {
      this.tipVisible = false;
      this.tipContent = "";
    },
    startScroll() {
      if (this.animationId) return;
      const step = () => {
        if (!this.isPaused && !this.isDragging && this.originalWidth > 0) {
          this.translateX -= this.scrollSpeed;
          if (this.translateX <= -this.originalWidth) {
            this.translateX += this.originalWidth;
          }
          if (this.translateX > 0) {
            this.translateX = 0;
          }
        }
        this.animationId = requestAnimationFrame(step);
      };
      this.animationId = requestAnimationFrame(step);
    },
    pauseScroll() {
      this.isPaused = true;
    },
    resumeScroll() {
      this.isPaused = false;
    },
    handleTouchStart(e) {
      if (e.touches.length === 1) {
        this.isDragging = false;
        this.touchStartX = e.touches[0].clientX;
        this.touchStartTranslateX = this.translateX;
      }
    },
    handleTouchMove(e) {
      if (e.touches.length === 1) {
        const deltaX = e.touches[0].clientX - this.touchStartX;
        if (Math.abs(deltaX) > 5) {
          this.isDragging = true;
        }
        let newX = this.touchStartTranslateX + deltaX;
        if (newX > 0) newX = 0;
        if (newX < -this.originalWidth) newX = -this.originalWidth;
        this.translateX = newX;
      }
    },
    handleTouchEnd() {
      setTimeout(() => {
        this.isDragging = false;
      }, 0);
    },
    updateOriginalWidth() {
      this.$nextTick(() => {
        const track = this.$refs.marqueeTrack;
        if (track) {
          this.originalWidth = track.scrollWidth / 2;
        }
      });
    },
    getCarouselInfo() {
      if (this.robotId === "exercises") {
        this.fetchExerciseRecommands();
      } else if (this.robotId === "mexercises") {
        this.fetchMExerciseRecommands();
      } else if (this.robotId === 'composition') {
        this.fetchExamRecommands();
      } else if (this.robotId === 'pexercises') {
        this.fetchPExerciseRecommands();
      }
    },
    async fetchExerciseRecommands() {
      const currentRequestId = ++this.requestId;
      let data = null;
      try {
        const response = await getExerciseRecommands();
        data = response.data;
      } catch (error) {
        console.error("获取练习推荐失败", error);
      }
      if (currentRequestId !== this.requestId) return;
      if (!data) {
        data = {
          windowInside: [
            ["推荐3道关于函数对称性的选择题", "生成2道数列新定义的解答题，难度较大", "推荐2道立体几何中的截面问题的题目", "推荐3道平面向量的参数范围问题的题目"],
            ["介绍裂项相消法", "齐次化方法专题报告", "介绍三次函数的图像与性质", "介绍直线与圆锥曲线的位置关系"],
            ["专题：不等式微专题专题", "专题：向量中的隐圆", "专题：数列奇偶项问题", "深度研究：椭圆中的仿射变换"],
            ["同一个坐标系绘制y=sinx与y=lgx的图像，x的范围为[0,20]", "绘制y=x+e^x的图像以及在点1处的切线", "绘制分段函数：f(x)=∣x−1∣,x>0; f(x)=-x^2-2x，x≤0的图像", "绘制z=x^2+y^3-x的3D图像"]
          ],
          windowOutside: ["高中图谱导航", "AI诊断", "一般化思想方法的图谱", "三次函数的图谱", "人教A版必修第一册的图谱", "立体几何中的折叠问题的图谱"]
        };
      }
      const questions = data.windowInside?.map(item => ({ questions: item })) || [];
      this.recommands = {
        questions: questions,
        infos: data.windowOutside || []
      };
      this.afterFetch();
    },
    async fetchMExerciseRecommands() {
      const currentRequestId = ++this.requestId;
      let data = null;
      try {
        const response = await getExerciseRecommands();
        data = response.data;
      } catch (error) {
        console.error("获取移动端练习推荐失败", error);
      }
      if (currentRequestId !== this.requestId) return;
      if (!data) {
        data = {
          windowInside: [
            ["推荐2道函数压轴选择题", "推荐1道数列综合解答题", "推荐3道三角函数图像变换题"],
            ["导数中隐零点专题", "圆锥曲线定点定值问题", "排列组合涂色问题"],
            ["每日一题：不等式恒成立", "每日一题：向量最值", "每日一题：概率与统计"],
            ["绘制f(x)=x^3-3x的图像", "绘制参数方程曲线"]
          ],
          windowOutside: ["思维导图：函数", "易错题集锦", "高频考点速递", "公式手册"]
        };
      }
      const questions = data.windowInside?.map(item => ({ questions: item })) || [];
      this.recommands = {
        questions: questions,
        infos: data.windowOutside || []
      };
      this.afterFetch();
    },
    async fetchPExerciseRecommands() {
      const currentRequestId = ++this.requestId;
      let data = null;
      try {
        const response = await getExerciseRecommands();
        data = response.data;
      } catch (error) {
        console.error("获取小学推荐失败", error);
      }
      if (currentRequestId !== this.requestId) return;
      if (!data) {
        data = {
          windowInside: [
            ["推荐2道加法口诀练习题", "生成3道简单乘法应用题", "推荐1道图形认识题", "找规律填数题2道"],
            ["如何计算长方形的周长", "认识钟表", "元角分换算练习", "长度单位换算"],
            ["每日一题：加减混合运算", "每日一题：比较大小", "每日一题：认识图形", "每日一题：时间计算"],
            ["绘制简单的条形图", "在数轴上标出数字"]
          ],
          windowOutside: ["小学知识点导航", "趣味数学故事", "口算速算技巧", "常考易错题"]
        };
      }
      const questions = data.windowInside?.map(item => ({ questions: item })) || [];
      this.recommands = {
        questions: questions,
        infos: data.windowOutside || []
      };
      this.afterFetch();
    },
    async fetchExamRecommands() {
      const currentRequestId = ++this.requestId;
      let data = null;
      try {
        const response = await getExamRecommands();
        data = response.data;
      } catch (error) {
        console.error("获取考试推荐失败", error);
      }
      if (currentRequestId !== this.requestId) return;
      if (!data) {
        data = {
          windowInside: [
            ["请以'AI与未来'为题写一篇议论文", "材料作文：'躺平'现象之我见", "命题作文：'我的高考'"],
            ["议论文写作结构指导", "素材积累：家国情怀", "高分作文开头技巧"],
            ["2025年高考作文预测", "传统文化与现代思辨", "时评类作文范例"]
          ],
          windowOutside: ["作文评分标准", "历年真题精选", "名言警句汇编", "热点话题清单"]
        };
      }
      const questions = data.windowInside?.map(item => ({ questions: item })) || [];
      this.recommands = {
        questions: questions,
        infos: data.windowOutside || []
      };
      this.afterFetch();
    },
    afterFetch() {
      this.translateX = 0;
      this.originalWidth = 0;
      this.updateOriginalWidth();
      if (!this.animationId) {
        this.startScroll();
      }
    }
  },
  watch: {
    robotId() {
      this.getCarouselInfo();
    },
    'recommands.questions': {
      deep: true,
      handler() {
        this.afterFetch();
      }
    }
  },
  mounted() {
    this.getCarouselInfo();
    this.startScroll();
    window.addEventListener('resize', this.updateOriginalWidth);
  },
  beforeDestroy() {
    if (this.animationId) cancelAnimationFrame(this.animationId);
    window.removeEventListener('resize', this.updateOriginalWidth);
  }
};
</script>

<style lang="scss" scoped>
/* ===== 布局容器：仅负责排列，无视觉样式 ===== */
.main-panel {
  margin: 0 auto;
  margin-top: 2%;
  width: 100%;
  max-width: 1100px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-family: 'Segoe UI', Roboto, sans-serif;
  position: relative; /* 用于弹窗定位 */
}

/* ===== 滚动区域（独立容器） ===== */
.marquee-wrapper {
  width: 100%;
  height: auto;
  overflow: hidden;
  border-radius: 18px;
  cursor: grab;
  display: flex;
  align-items: flex-start;
  padding: 14px 0;
  background: radial-gradient(ellipse at 50% 30%, rgba(255, 255, 255, 0.8) 0%, rgba(220, 235, 250, 0.7) 100%);
  background-image: 
    radial-gradient(ellipse at 50% 30%, rgba(255, 255, 255, 0.8) 0%, rgba(220, 235, 250, 0.7) 100%),
    linear-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.4) 1px, transparent 1px);
  background-size: 
    100% 100%,
    20px 20px,
    20px 20px;
  box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.03), 0 4px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.6);
  
  &:active {
    cursor: grabbing;
  }
}

.marquee-track {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  height: auto;
  padding: 0;
  will-change: transform;
  transform: translateZ(0);
}

.rows-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
}

.card-row {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}

/* ===== 卡片美化 ===== */
.question-card {
  display: inline-flex;
  align-items: center;
  border-radius: 100px;
  padding: 0 18px;
  min-height: 40px;
  font-size: 13px;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease, border-color 0.25s ease;
  width: auto;
  flex-shrink: 0;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02);

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    margin-right: 7px;
    transition: background 0.25s ease;
  }
  
  &:nth-child(5n+1) {
    background: rgba(166, 227, 255, 0.3);
    &::before { background: #4da8d8; }
  }
  &:nth-child(5n+2) {
    background: rgba(173, 255, 183, 0.3);
    &::before { background: #4cba5e; }
  }
  &:nth-child(5n+3) {
    background: rgba(255, 224, 130, 0.3);
    &::before { background: #d4a017; }
  }
  &:nth-child(5n+4) {
    background: rgba(255, 182, 193, 0.3);
    &::before { background: #d4677a; }
  }
  &:nth-child(5n+5) {
    background: rgba(212, 193, 255, 0.3);
    &::before { background: #8b6cc7; }
  }

  &:hover {
    background: #ffffff !important;
    border-color: rgba(26, 32, 41, 0.15);
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.1);
    transform: translateY(-3px);
  }
}

.card-text {
  color: #1a1a2e;
  font-weight: 600;
  position: relative;
  z-index: 1;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
}

/* ===== 底部分组信息（独立容器） ===== */
.bottom-info {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 13px;
  color: #333333;
  flex-shrink: 0;
  padding: 12px 16px;
  background: radial-gradient(ellipse at 50% 30%, rgba(255, 255, 255, 0.7) 0%, rgba(220, 235, 250, 0.6) 100%);
  border-radius: 14px;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.6);

  .info-group {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .group-tag {
      font-size: 12px;
      color: rgb(100, 3, 211);
      background: rgba(255, 255, 255, 0.5);
      border: 1px dashed rgb(166, 161, 196);
      border-radius: 8px;
      padding: 4px 8px;
      white-space: nowrap;
      font-weight: 500;
      letter-spacing: 0.3px;
      flex-shrink: 0;
      cursor: default;
      transition: all 0.25s ease;

      /* 有提示词的标签样式（去掉下划线） */
      &.has-tip {
        cursor: pointer;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(220, 235, 250, 0.9) 100%);
        border: 1px solid #7c6fd0;
        color: #5b4aa8;
        box-shadow: 0 2px 6px rgba(124, 111, 208, 0.2);
        
        &:hover {
          background: rgba(255, 255, 255, 1);
          border-color: #5b4aa8;
          transform: translateY(-1px);
          box-shadow: 0 4px 10px rgba(124, 111, 208, 0.3);
        }
      }
    }
    
    .group-items {
      display: flex;
      gap: 8px;
      flex-wrap: nowrap;
    }
  }

  .info {
    padding: 6px 12px;
    border: 1px solid rgb(191, 196, 241);
    border-radius: 12px;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(6px);
    transition: all 0.25s ease;
    white-space: nowrap;
    font-size: 13px;

    &:hover {
      border-color: var(--txt-color-blue);
      color: var(--txt-color-blue);
      background: rgba(255, 255, 255, 0.9);
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }
  }
}

/* ===== 自定义提示弹窗 ===== */
.tip-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(2px);
}

.tip-dialog {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 420px;
  padding: 20px;
  position: relative;
  animation: tipIn 0.2s ease;
}

.tip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.tip-title {
  font-size: 16px;
  font-weight: 600;
  color: #5b4aa8;
}

.tip-close {
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: #999;
  transition: color 0.2s;
  padding: 0 4px;
  
  &:hover {
    color: #333;
  }
}

.tip-body {
  font-size: 14px;
  line-height: 1.6;
  color: #444;
  white-space: pre-wrap;
  word-break: break-word;
}

/* 弹窗过渡动画 */
.tip-fade-enter-active,
.tip-fade-leave-active {
  transition: opacity 0.2s;
}
.tip-fade-enter,
.tip-fade-leave-to {
  opacity: 0;
}

@keyframes tipIn {
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

/* ===== 移动端适配 ===== */
.isMobile.main-panel {
  font-size: $font_size;
  max-width: 95vw;
  min-width: 360px;
  padding: 10px;
  overflow: visible;
  gap: 10px;
}

.isMobile .marquee-wrapper {
  min-height: auto;
  padding: 12px 0;
  background-size: 100% 100%, 16px 16px, 16px 16px;
}

.isMobile .bottom-info {
  font-size: clamp(10px, 0.14rem, 16px);
  gap: 5px;
  flex-direction: column;
  align-items: flex-start;
  padding: 8px;
  background: radial-gradient(ellipse at 50% 30%, rgba(255, 255, 255, 0.7) 0%, rgba(220, 235, 250, 0.6) 100%);
}

.isMobile .info-group {
  gap: 4px;
  flex-wrap: wrap;
}

.isMobile .group-tag {
  font-size: 9px;
  padding: 2px 6px;
  border: 1px dashed #7c6fd0;
  color: #5b4aa8;
  
  &.has-tip {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(220, 235, 250, 0.9) 100%);
    border: 1px solid #7c6fd0;
    color: #5b4aa8;
    /* 无下划线 */
  }
}

.isMobile .group-items {
  gap: 5px;
  flex-wrap: wrap;
}

.isMobile .info {
  padding: 5px 8px;
  font-size: 10px;
  white-space: normal;
  word-break: break-word;
}

.isMobile .question-card {
  font-size: clamp(12px, 0.14rem, 16px);
  white-space: normal;
  word-break: break-word;
  padding: 6px 12px;
  min-height: 36px;
  
  &::before {
    width: 6px;
    height: 6px;
    margin-right: 6px;
  }
}
</style>